// AANGraphs.tsx
/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useEffect, useMemo, useState, useCallback } from 'react';
import type { TFunction } from 'i18next';
import MultiLineChart from '../../../../../../../components/multi-line-chart/multi-line-chart';
import BrushGraphs from '../../../../../../../../app/components/brush-graphs/brush-graphs';
import { RadioButton } from 'primereact/radiobutton';
import { ResponsiveContainer } from 'recharts';
import LineChart from '../../kinematic-graphs/kinematic-graphs';
import EmptyState from '../graphs/empty-grphs';

type JointKey = 'hipL' | 'hipR' | 'kneeL' | 'kneeR';

type AANData = {
  assistanceLevels: any[] | null;
  joint: {
    hipL: any[] | null;
    hipR: any[] | null;
    kneeL: any[] | null;
    kneeR: any[] | null;
  };
  assistedTorque: any[] | null;
  kinematics: {
    leftHip: any | null;
    rightHip: any | null;
    leftKnee: any | null;
    rightKnee: any | null;
  };
};

type Props = {
  t: TFunction<'translation'>;
  aanData: AANData | undefined;

  // brush controlado desde el padre
  startIndex: number;
  endIndex: number;
  onBrushChange: (r: { startIndex: number; endIndex: number }) => void;
  onBaseLenChange: (len: number) => void;
};

const noop: (loading: boolean) => void = () => {
  /*Intentionally empty*/
};

function hasValidData(d: any) {
  if (!d) return false;
  if (Array.isArray(d)) return d.length > 0;
  if (typeof d === 'object') return Object.keys(d).length > 0;
  return false;
}

const AANGraphsImpl = ({
  t,
  aanData,
  startIndex,
  endIndex,
  onBrushChange,
  onBaseLenChange,
}: Props) => {
  const [selectedJoint, setSelectedJoint] = useState<JointKey>('hipL');

  const data = useMemo(
    () => ({
      assistanceLevels: aanData?.assistanceLevels ?? [],
      joint: {
        hipL: aanData?.joint?.hipL ?? [],
        hipR: aanData?.joint?.hipR ?? [],
        kneeL: aanData?.joint?.kneeL ?? [],
        kneeR: aanData?.joint?.kneeR ?? [],
      },
      assistedTorque: aanData?.assistedTorque ?? [],
      kinematics: {
        leftHip: aanData?.kinematics?.leftHip ?? null,
        rightHip: aanData?.kinematics?.rightHip ?? null,
        leftKnee: aanData?.kinematics?.leftKnee ?? null,
        rightKnee: aanData?.kinematics?.rightKnee ?? null,
      },
    }),
    [aanData]
  );

  // base para el brush: como en tu padre
  const base =
    (data.assistanceLevels.length ? data.assistanceLevels : null) ??
    (data.joint[selectedJoint].length ? data.joint[selectedJoint] : null) ??
    (data.assistedTorque.length ? data.assistedTorque : null) ??
    [];

  useEffect(() => {
    onBaseLenChange(base.length || 0);
  }, [base.length, onBaseLenChange]);

  // slices filtrados
  const filteredAssistance = useMemo(() => {
    const src = data.assistanceLevels;
    return src.slice(startIndex, endIndex + 1);
  }, [data.assistanceLevels, endIndex, startIndex]);

  const filteredJointSeries = useMemo(() => {
    const src = data.joint[selectedJoint];
    return src.slice(startIndex, endIndex + 1);
  }, [data.joint, endIndex, selectedJoint, startIndex]);

  const filteredAssistanceTorque = useMemo(() => {
    const src = data.assistedTorque;
    return src.slice(startIndex, endIndex + 1);
  }, [data.assistedTorque, endIndex, startIndex]);

  const jointOptions = useMemo(
    () => [
      {
        label: t('pages.patients.viewPatient.sessions.ci_expanded'),
        value: 'hipL',
      },
      {
        label: t('pages.patients.viewPatient.sessions.cd_expanded'),
        value: 'hipR',
      },
      {
        label: t('pages.patients.viewPatient.sessions.ri_expanded'),
        value: 'kneeL',
      },
      {
        label: t('pages.patients.viewPatient.sessions.rd_expanded'),
        value: 'kneeR',
      },
    ],
    [t]
  );

  const onJointChange = useCallback((e: { value: JointKey }) => {
    setSelectedJoint(e.value);
  }, []);

  const showSomething =
    hasValidData(filteredAssistance) ||
    hasValidData(filteredJointSeries) ||
    hasValidData(filteredAssistanceTorque) ||
    [
      data.kinematics?.leftHip?.media_ciclo,
      data.kinematics?.rightHip?.media_ciclo,
      data.kinematics?.leftKnee?.media_ciclo,
      data.kinematics?.rightKnee?.media_ciclo,
    ].every(hasValidData);

  if (!showSomething) {
    return <EmptyState icon="pi pi-chart-line" title={t('emptyMessage')} />;
  }

  const showBrush = base.length > 0;

  return (
    <>
      {/* 1) Cinemática (media de ciclo) */}
      {[
        aanData.kinematics?.leftHip?.media_ciclo,
        aanData.kinematics?.rightHip?.media_ciclo,
        aanData.kinematics?.leftKnee?.media_ciclo,
        aanData.kinematics?.rightKnee?.media_ciclo,
      ].every(hasValidData) && (
        <div className="mt-5">
          <h3 className="md:text-2xl font-bold mb-4">
            {t('pages.patients.viewPatient.sessions.gaitKinematicsAnalysis')}
          </h3>
          <div className="flex flex-wrap" style={{ justifyContent: 'center' }}>
            <div className="w-12 md:w-6 mb-6">
              <div className="surface-100 h-full container-graphs__shadow-customs-graphs md:p-2 p-1 border-round-lg text-center">
                <ResponsiveContainer width="100%" height={500}>
                  <LineChart
                    data={data.kinematics?.leftHip}
                    title={t('pages.patients.viewPatient.sessions.fe_hl')}
                    selectedVar="hip_l"
                    setLoading={noop}
                  />
                </ResponsiveContainer>
              </div>
            </div>
            <div className="w-12 md:w-6 mb-6">
              <div className="md:ml-4 surface-100 h-full container-graphs__shadow-customs-graphs md:p-2 p-1 border-round-lg text-center">
                <ResponsiveContainer width="100%" height={500}>
                  <LineChart
                    data={data.kinematics?.rightHip}
                    title={t('pages.patients.viewPatient.sessions.fe_hr')}
                    selectedVar="hip_r"
                    setLoading={noop}
                  />
                </ResponsiveContainer>
              </div>
            </div>
            <div className="w-12 md:w-6 mb-6">
              <div className="surface-100 h-full container-graphs__shadow-customs-graphs md:p-2 p-1 border-round-lg text-center">
                <ResponsiveContainer width="100%" height={500}>
                  <LineChart
                    data={data.kinematics?.leftKnee}
                    title={t('pages.patients.viewPatient.sessions.fe_kl')}
                    selectedVar="knee_l"
                    setLoading={noop}
                  />
                </ResponsiveContainer>
              </div>
            </div>
            <div className="w-12 md:w-6 mb-6">
              <div className="md:ml-4 surface-100 h-full container-graphs__shadow-customs-graphs md:p-2 p-1 border-round-lg text-center">
                <ResponsiveContainer width="100%" height={500}>
                  <LineChart
                    data={data.kinematics?.rightKnee}
                    title={t('pages.patients.viewPatient.sessions.fe_kr')}
                    selectedVar="knee_r"
                    setLoading={noop}
                  />
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBrush && (
        <div className="sticky z-1" style={{ top: '90px' }}>
          <BrushGraphs
            data={base}
            startIndex={startIndex}
            endIndex={endIndex}
            onBrushChange={onBrushChange}
          />
        </div>
      )}

      <div className="col-12 flex flex-column gap-2 mb-2" />

      {/* 2) Nivel de asistencia */}
      {hasValidData(filteredAssistance) && (
        <MultiLineChart
          data={filteredAssistance}
          title={t('pages.patients.viewPatient.sessions.assistanceLevel')}
          setLoading={noop}
        />
      )}

      {/* 3) Posición articular + radios */}
      {hasValidData(filteredJointSeries) && (
        <div className="mt-5">
          <h3 className="text-2xl font-bold mb-2">
            {t('pages.patients.viewPatient.sessions.jointPosition')}
          </h3>

          <div className="flex flex-wrap gap-3 items-center my-3">
            {jointOptions.map((opt) => (
              <div key={opt.value} className="flex items-center gap-2 ml-2">
                <RadioButton
                  inputId={`joint-${opt.value}`}
                  name="joint"
                  value={opt.value}
                  onChange={onJointChange}
                  checked={selectedJoint === opt.value}
                />
                <label htmlFor={`joint-${opt.value}`} className="text-sm">
                  {opt.label}
                </label>
              </div>
            ))}
          </div>

          <MultiLineChart
            data={filteredJointSeries}
            title={''}
            setLoading={noop}
          />
        </div>
      )}

      {/* 4) Fuerza de asistencia */}
      {hasValidData(filteredAssistanceTorque) && (
        <MultiLineChart
          data={filteredAssistanceTorque}
          title={t('pages.patients.viewPatient.sessions.assistedTorque')}
          setLoading={noop}
        />
      )}
    </>
  );
};

export default memo(AANGraphsImpl);
